/**
 * AI Visibility offer landing page (Sprint 3B).
 *
 * A calm, informational page that continues the Monday Brief experience.
 * Reachable from:
 *   - The "See how we can help" CTA inside the Monday Brief AI Discovery
 *     section (email link → /offers/ai-visibility/:businessId)
 *   - The AI Discovery Check panel on the post-onboarding Results page
 *
 * Guardrails:
 *   - No hard sell, no fake urgency, no fake Stripe / payment surfaces.
 *   - Never states that AI recommendations can be purchased or guaranteed.
 *   - Reuses the app's design tokens so it feels native to Your Monday Brief.
 *
 * The primary CTA records a `service_requests` row (offerType
 * = AI_VISIBILITY_INTEREST) via `createOfferInterest`. Repeat clicks
 * within a short window are deduped server-side.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import {
  getOfferBusinessContext,
  createOfferInterest,
} from "@/lib/api";

const WHAT_WE_LOOK_AT = [
  {
    title: "Business information accuracy",
    body: "Name, address, phone number, hours, and services should read the same way everywhere.",
  },
  {
    title: "Website clarity",
    body: "Is your site written so an AI model can understand what you do, where you serve, and who you help?",
  },
  {
    title: "Google Business Profile signals",
    body: "Is your profile connected, populated, and consistent with your website and social pages?",
  },
  {
    title: "Customer reviews",
    body: "Real reviews from real customers on the sites AI systems trust as independent sources.",
  },
  {
    title: "Online reputation signals",
    body: "How the wider web describes your business — directories, third-party mentions, and citations.",
  },
  {
    title: "Consistency across the web",
    body: "Small mismatches (address typos, old phone numbers) can quietly reduce confidence in your listing.",
  },
];

const HOW_WE_HELP = [
  { title: "Identify gaps", body: "We show you exactly where AI systems disagree or come up short." },
  { title: "Improve business information", body: "Get every listing telling the same accurate story." },
  { title: "Strengthen online presence", body: "Build the signals AI systems rely on to recommend you." },
  { title: "Monitor changes", body: "Re-check your AI visibility over time so you can see progress." },
];

/** Small pill row for a single provider's simplified verdict. */
function ProviderCard({ p }) {
  const rows = [
    { label: "Recognized your business", value: p.businessRecognized },
    { label: "Found your website", value: p.websiteFound },
    { label: "Saw your Google Profile", value: p.googleProfileFound },
    { label: "Confidence", value: p.confidence },
  ];
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: "var(--ta-surface)",
        border: "1px solid var(--ta-border)",
      }}
      data-testid={`ai-visibility-offer-provider-${p.provider}`}
    >
      <div
        className="text-sm font-semibold mb-3"
        style={{ color: "var(--ta-primary)" }}
      >
        {p.providerDisplayName}
      </div>
      <dl className="space-y-2 text-sm">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between gap-3">
            <dt style={{ color: "var(--ta-text-secondary)" }}>{r.label}</dt>
            <dd
              className="font-medium text-right"
              style={{ color: "var(--ta-primary)" }}
              data-testid={`ai-visibility-offer-provider-${p.provider}-${r.label
                .toLowerCase()
                .replace(/[^a-z]+/g, "-")}`}
            >
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function AiVisibilityOffer() {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(Boolean(businessId));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!businessId) return;
    (async () => {
      try {
        const data = await getOfferBusinessContext(businessId);
        if (alive) setCtx(data);
      } catch {
        // Silently continue — the page still renders without context.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [businessId]);

  const goBack = () => {
    if (businessId) navigate(`/onboarding/results/${businessId}`);
    else navigate(-1);
  };

  const onCtaClick = async () => {
    if (!businessId) {
      // Anonymous landing — no request record created. Just acknowledge.
      toast.info(
        "Thanks for your interest. Complete your Monday Brief signup so we can review your business."
      );
      return;
    }
    setSubmitting(true);
    try {
      await createOfferInterest({
        businessId,
        offerType: "AI_VISIBILITY_INTEREST",
        sourcePage: `/offers/ai-visibility/${businessId}`,
      });
      setSubmitted(true);
      toast.success(
        "Thanks — we'll be in touch. No payment or account changes were made."
      );
    } catch {
      toast.error("We couldn't record your request. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const providers = useMemo(
    () => (ctx?.aiDiscovery?.providers ?? []).slice(0, 3),
    [ctx],
  );
  const hasAi = providers.length > 0;

  return (
    <AppShell>
      <div
        className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14 space-y-10"
        data-testid="ai-visibility-offer-page"
      >
        <button
          data-testid="ai-visibility-offer-back"
          onClick={goBack}
          className="text-sm underline"
          style={{ color: "var(--ta-text-secondary)" }}
        >
          ← Back
        </button>

        <header className="space-y-4">
          <div
            className="inline-block text-xs uppercase tracking-widest px-3 py-1 rounded-full"
            style={{
              background: "var(--ta-surface-alt)",
              color: "var(--ta-text-secondary)",
              border: "1px solid var(--ta-border)",
            }}
            data-testid="ai-visibility-offer-eyebrow"
          >
            AI Visibility · From Your Monday Brief
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight"
            style={{ color: "var(--ta-primary)" }}
            data-testid="ai-visibility-offer-headline"
          >
            Is AI ready to recommend your business?
          </h1>
          <p
            className="text-base sm:text-lg leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="ai-visibility-offer-subhead"
          >
            More customers are beginning to ask AI tools for recommendations
            instead of traditional searches. We help local businesses
            understand what AI systems know about them and identify
            opportunities to improve their visibility.
          </p>
        </header>

        {/* Current AI Discovery results. */}
        {hasAi && (
          <section
            className="space-y-5"
            data-testid="ai-visibility-offer-current-results"
          >
            <div className="space-y-1">
              <h2
                className="text-lg font-semibold"
                style={{ color: "var(--ta-primary)" }}
              >
                What we saw in your AI Discovery check
              </h2>
              <p className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>
                A calm summary of what each system said. No jargon, no scores.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {providers.map((p) => (
                <ProviderCard key={p.provider} p={p} />
              ))}
            </div>

            {ctx?.aiDiscovery?.strongestPositive && (
              <div
                className="rounded-2xl p-5"
                style={{
                  background: "var(--ta-surface-alt)",
                  border: "1px solid var(--ta-border)",
                }}
              >
                <div
                  className="text-xs uppercase tracking-widest mb-1"
                  style={{ color: "var(--ta-text-secondary)" }}
                >
                  Good news
                </div>
                <div
                  className="text-sm"
                  style={{ color: "var(--ta-primary)" }}
                  data-testid="ai-visibility-offer-strongest"
                >
                  {ctx.aiDiscovery.strongestPositive}
                </div>
              </div>
            )}
            {ctx?.aiDiscovery?.mostImportantOpportunity && (
              <div
                className="rounded-2xl p-5"
                style={{
                  background: "var(--ta-surface-alt)",
                  border: "1px solid var(--ta-border)",
                }}
              >
                <div
                  className="text-xs uppercase tracking-widest mb-1"
                  style={{ color: "var(--ta-text-secondary)" }}
                >
                  An opportunity to improve
                </div>
                <div
                  className="text-sm"
                  style={{ color: "var(--ta-primary)" }}
                  data-testid="ai-visibility-offer-opportunity"
                >
                  {ctx.aiDiscovery.mostImportantOpportunity}
                </div>
              </div>
            )}
          </section>
        )}

        {loading && !hasAi && (
          <div
            className="rounded-2xl p-5 text-sm"
            style={{
              background: "var(--ta-surface-alt)",
              border: "1px solid var(--ta-border)",
              color: "var(--ta-text-secondary)",
            }}
            data-testid="ai-visibility-offer-loading"
          >
            Loading your AI Discovery results…
          </div>
        )}

        {/* Short video coming soon. */}
        <div
          data-testid="ai-visibility-offer-video-placeholder"
          className="w-full aspect-video rounded-2xl flex items-center justify-center"
          style={{
            background: "var(--ta-surface-alt)",
            border: "1px dashed var(--ta-border)",
            color: "var(--ta-text-secondary)",
          }}
        >
          <div className="text-center px-6">
            <div
              className="text-xs uppercase tracking-widest mb-2"
              style={{ color: "var(--ta-text-secondary)" }}
            >
              Short video coming soon
            </div>
            <div className="text-sm">
              A walkthrough from our team will live here.
            </div>
          </div>
        </div>

        {/* What we look at. */}
        <section
          className="space-y-5"
          data-testid="ai-visibility-offer-what-we-look-at"
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: "var(--ta-primary)" }}
          >
            What we look at
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHAT_WE_LOOK_AT.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl p-5"
                style={{
                  background: "var(--ta-surface)",
                  border: "1px solid var(--ta-border)",
                }}
              >
                <div
                  className="text-sm font-semibold mb-1"
                  style={{ color: "var(--ta-primary)" }}
                >
                  {item.title}
                </div>
                <div
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--ta-text-secondary)" }}
                >
                  {item.body}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How we help. */}
        <section
          className="rounded-2xl p-6 space-y-4"
          style={{
            background: "var(--ta-surface)",
            border: "1px solid var(--ta-border)",
          }}
          data-testid="ai-visibility-offer-how-we-help"
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: "var(--ta-primary)" }}
          >
            How we help
          </h2>
          <ul
            className="space-y-3 text-sm leading-relaxed"
            style={{ color: "var(--ta-primary)" }}
          >
            {HOW_WE_HELP.map((item) => (
              <li key={item.title}>
                <span className="font-semibold">{item.title}</span> — {item.body}
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <div className="text-center space-y-3">
          <button
            data-testid="ai-visibility-offer-cta"
            onClick={onCtaClick}
            disabled={submitting || submitted}
            className="px-8 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
            style={{ background: "var(--ta-primary)", color: "white" }}
          >
            {submitted
              ? "Request received — we'll be in touch"
              : submitting
              ? "Sending your request…"
              : "Request a free AI visibility review"}
          </button>
          <p
            className="text-xs"
            style={{ color: "var(--ta-text-secondary)" }}
          >
            No payment or account changes happen when you click this.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
